<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="text-center">
    <v-dialog
      v-model="dialog"
      width="500"
      :fullscreen="$vuetify.display.smAndDown"
    >
      <template v-slot:activator="{ props }">
        <v-btn
          color="primary"
          variant="text"
          v-bind="props"
        >
          <v-icon>mdi-plus</v-icon>
          <span class="add-account">{{ $t('labels.add-account') }}</span>
        </v-btn>
      </template>

      <v-card class="d-flex flex-column">
        <v-card-title class="pa-0">
          <v-toolbar
            color="primary"
            dark
            flat
          >
            <v-toolbar-title class="link-accounts-title">
              {{ $t('social-media.link-accounts') }}
            </v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <!-- TODO: check unsupported prop: dark -->
              <v-btn
                icon
                @click="close"
              >
                <v-icon size="large"> mdi-close </v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
        </v-card-title>

        <v-card-text v-if="connectingTo">
          <ProgressCircular />
          <div class="text-center connected-pages">
            {{
              $t('alerts.social-media.connecting-to-social-media.info', { platform: connectingTo })
            }}
          </div>
        </v-card-text>

        <v-card-text
          v-else
          class="mt-5 d-flex flex-column gap-1"
        >
          <template v-if="!allPlatformsConnected">
            <div class="connected-pages">
              {{
                $t('social-media.choose-the-platform-to-connect-and-post', { readablePlatformName })
              }}
            </div>
            <BlurredBackdropUpselling
              :is-blurred="!postToFacebook"
              margin-top="80px"
              min-height="180px"
            >
              <div
                v-if="metaPages.length === 0"
                class="w-full"
              >
                <ConnectFacebook
                  class="w-full"
                  :connect="connectFacebook"
                  @featureEnabled="postToFacebook = $event"
                />
                <v-alert
                  v-if="platformError.Facebook"
                  type="warning"
                  density="compact"
                  tile
                  text
                  class="mb-0 newline"
                >
                  <div
                    v-html="
                      $t('alerts.social-media.no-pages-found.facebook', {
                        link: '<a target=\'_blank\' href=\'https://www.facebook.com/pages/creation/\'>Facebook Pages</a>',
                      })
                    "
                  />
                </v-alert>
              </div>
            </BlurredBackdropUpselling>

            <BlurredBackdropUpselling
              :is-blurred="!postToInstagram"
              margin-top="120px"
              min-height="260px"
            >
              <ConnectInstagram
                v-if="!socialMediaConnections.includes('INSTAGRAM')"
                :is-facebook-connected="socialMediaConnections.includes('FACEBOOK')"
                :connect="connectInstagram"
                @featureEnabled="postToInstagram = $event"
              />
              <v-alert
                v-if="platformError.Instagram"
                type="warning"
                density="compact"
                tile
                text
                class="mb-0 newline"
              >
                <div
                  v-html="
                    $t('alerts.social-media.no-pages-found.instagram', {
                      businessSuiteLink:
                        '<a target=\'_blank\' href=\'https://business.facebook.com/latest/settings/instagram_account?selected_asset_type=instagram-account-v2&detail_view_tab=CONNECTED_ASSETS\'>Meta-Business-Suite</a>',
                    })
                  "
                />
              </v-alert>
            </BlurredBackdropUpselling>

            <BlurredBackdropUpselling
              :is-blurred="!postToLinkedin"
              margin-top="80px"
              min-height="180px"
            >
              <div
                v-if="linkedinPages.length === 0"
                class="w-full"
              >
                <ConnectLinkedIn
                  class="w-full"
                  :connect="connectLinkedin"
                  @featureEnabled="postToLinkedin = $event"
                />
                <v-alert
                  v-if="platformError.LinkedIn"
                  type="warning"
                  density="compact"
                  tile
                  text
                  class="mb-0 newline"
                >
                  <div
                    v-html="
                      $t('alerts.social-media.no-pages-found.linkedin', {
                        link: '<a target=\'_blank\' href=\'https://www.linkedin.com/company/setup/new/\'>LinkedIn Pages</a>',
                      })
                    "
                  />
                </v-alert>
              </div>
            </BlurredBackdropUpselling>

            <BlurredBackdropUpselling
              v-if="canUseGoogleBusiness"
              :is-blurred="!postToGoogleBusiness"
              margin-top="80px"
              min-height="180px"
            >
              <div
                v-if="!isGoogleBusinessConnected"
                class="w-full"
              >
                <ConnectGoogleBusiness
                  class="w-full"
                  :connection-wrapper="connectGoogleBusiness"
                  :feature-config="featureConfig"
                  @featureEnabled="postToGoogleBusiness = $event"
                />
                <v-alert
                  v-if="platformError[googleBusinessPlatformKey]"
                  type="error"
                  density="compact"
                  tile
                  text
                  class="mb-0 newline"
                >
                  {{
                    $t('alerts.social-media.connecting-to-social-media.error', {
                      platform: googleBusinessPlatformLabel,
                    })
                  }}
                </v-alert>
              </div>
            </BlurredBackdropUpselling>
          </template>

          <SocialMediaAccountPicker
            v-if="socialMediaConnections.includes('FACEBOOK')"
            :provider="SocialMediaType.Facebook"
            :options="selectionOptionsFor(SocialMediaType.Facebook)"
            :loading="selectingOption.Facebook"
            auto-apply
            @select="selectFacebookPage"
          />
          <SocialMediaAccountPicker
            v-if="socialMediaConnections.includes('INSTAGRAM')"
            :provider="SocialMediaType.Instagram"
            :options="selectionOptionsFor(SocialMediaType.Instagram)"
            :loading="selectingOption.Instagram"
            auto-apply
            @select="selectInstagramPage"
          />
          <SocialMediaAccountPicker
            v-if="socialMediaConnections.includes('LINKEDIN')"
            :provider="SocialMediaType.Linkedin"
            :options="selectionOptionsFor(SocialMediaType.Linkedin)"
            :loading="selectingOption.LinkedIn"
            auto-apply
            @select="selectLinkedinPage"
          />
          <SocialMediaAccountPicker
            v-if="isGoogleBusinessConnected"
            :provider="SocialMediaType.GoogleBusiness"
            :options="selectionOptionsFor(SocialMediaType.GoogleBusiness)"
            auto-apply
            @select="selectGoogleBusinessLocation"
          />
        </v-card-text>

        <v-card-actions>
          <v-btn
            variant="text"
            class="close-btn"
            @click="close"
          >
            {{ $t('labels.close') }}
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            class="proceed-btn"
            variant="text"
            @click="close"
          >
            {{ $t('labels.proceed') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import BlurredBackdropUpselling from '@/components/BlurredBackdropUpselling.vue'
import ProgressCircular from '@/components/ProgressCircular.vue'
import SocialMediaAccountPicker from '@/components/SocialMediaAccountPicker.vue'
import { useSocialMedia } from '@/composables/useSocialMedia'
import { SocialMediaType } from '@/generated/graphql'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import {
  getConnectionHelpCenterUrl,
  getSocialMediaConnectionErrorDisplay,
} from '@/lib/socialMediaConnectionError'
import branding from '@/mixins/branding'
import feature from '@/mixins/feature'

import { ReadableSocialMedia, SocialMedia } from '../../enums/SocialMedia'
import ConnectFacebook from './ConnectFacebook.vue'
import ConnectGoogleBusiness from './ConnectGoogleBusiness.vue'
import ConnectInstagram from './ConnectInstagram.vue'
import ConnectLinkedIn from './ConnectLinkedIn.vue'

// These get their own richer, link-embedding inline alert (below) instead of the generic snackbar.
const NO_PAGES_CODES = ['FACEBOOK_NO_PAGES', 'INSTAGRAM_NOT_BUSINESS_ACCOUNT', 'LINKEDIN_NO_PAGES']

export default {
  components: {
    ProgressCircular,
    SocialMediaAccountPicker,
    BlurredBackdropUpselling,
    ConnectLinkedIn,
    ConnectFacebook,
    ConnectInstagram,
    ConnectGoogleBusiness,
  },
  mixins: [branding, feature],
  setup() {
    const {
      socialMediaConnections,
      loadSocialMediaConnections,
      metaPages,
      loadMetaPages,
      linkedinPages,
      loadLinkedinPages,
      connecting,
      connectedSuccessfully,
      error,
      contentCreatorFeature,
      connectToLinkedin,
      connectToFacebook,
      connectToInstagram,
      canUseGoogleBusiness,
      connectToGoogleBusiness,
      isGoogleBusinessConnected,
      loadGoogleBusinessStatus,
      googleBusinessLocations,
      googleBusinessLocationsLoading,
      loadGoogleBusinessLocations,
      selectionOptionsFor,
      needsSelectionFor,
      selectOption,
    } = useSocialMedia()
    const { locale } = useI18n()

    // Correction 1: ConnectGoogleBusiness.vue still requires a `featureConfig` prop
    // (`featureConfig?.postToGoogleBusiness`), even though the other three Connect*
    // buttons no longer need one. Re-derive it from the composable's
    // `contentCreatorFeature` so `:feature-config="featureConfig"` below keeps working
    // exactly as it did when it was populated by the old local `created()` fetch.
    const featureConfig = computed(() => contentCreatorFeature.value?.config ?? null)

    return {
      socialMediaConnections,
      loadSocialMediaConnections,
      metaPages,
      loadMetaPages,
      linkedinPages,
      loadLinkedinPages,
      connecting,
      connectedSuccessfully,
      composableError: error,
      featureConfig,
      connectToLinkedin,
      connectToFacebook,
      connectToInstagram,
      canUseGoogleBusiness,
      connectToGoogleBusiness,
      isGoogleBusinessConnected,
      loadGoogleBusinessStatus,
      googleBusinessLocations,
      googleBusinessLocationsLoading,
      loadGoogleBusinessLocations,
      selectionOptionsFor,
      needsSelectionFor,
      selectOption,
      locale,
      SocialMediaType,
    }
  },
  data() {
    return {
      dialog: false,
      connectingPlatform: null,
      postToFacebook: true,
      postToInstagram: true,
      postToLinkedin: true,
      postToGoogleBusiness: true,
      platformError: {},
      selectingOption: { Facebook: false, Instagram: false, LinkedIn: false },
    }
  },
  computed: {
    googleBusinessPlatformKey() {
      return SocialMedia.GOOGLE_BUSINESS
    },
    googleBusinessPlatformLabel() {
      return ReadableSocialMedia.GOOGLE_BUSINESS
    },
    helpCenterLinkText() {
      return {
        text: this.$t('alerts.social-media.connection-error.help-center-link'),
        link: getConnectionHelpCenterUrl(this.locale),
      }
    },
    allPlatformsConnected() {
      const googleBusinessSettled = !this.canUseGoogleBusiness || this.isGoogleBusinessConnected
      return this.socialMediaConnections.length === 3 && googleBusinessSettled
    },
    connectingTo() {
      return this.connecting ? this.connectingPlatform : null
    },
  },
  async created() {
    // Await the connections query before deciding whether to load the page/org lists —
    // mounted() runs before an async created() resolves, so checking
    // socialMediaConnections there would race against this query (and the parent's own
    // in-flight GET_SOCIAL_MEDIA_CONNECTIONS) and see a still-empty array.
    await this.loadSocialMediaConnections()
    if (this.socialMediaConnections.includes('FACEBOOK')) this.loadMetaPages()
    if (this.socialMediaConnections.includes('LINKEDIN')) this.loadLinkedinPages()
  },
  mounted() {
    if (this.canUseGoogleBusiness) this.loadGoogleBusinessStatus()
  },
  watch: {
    dialog(isOpen) {
      if (isOpen && this.canUseGoogleBusiness) this.loadGoogleBusinessStatus()
    },
    isGoogleBusinessConnected: {
      handler(connected) {
        if (connected) this.loadGoogleBusinessLocations()
      },
      immediate: true,
    },
    // The composable's error/connectedSuccessfully are a single shared value across all
    // four platforms (unlike the old per-platform map) — attribute them to whichever
    // platform this dialog most recently tried to connect. See plan Review Focus.
    // Google Business is excluded: connectGoogleBusiness() already owns its full
    // error/success handling (including waiting for the status poll to actually confirm
    // CONNECTED before calling it a success), so letting these watchers also react to it
    // would double-handle it and could report success before the poll resolves.
    composableError(err) {
      if (!err || !this.connectingPlatform) return
      if (this.connectingPlatform === this.googleBusinessPlatformLabel) return
      const platform = this.connectingPlatform
      const { code, severity, messageKey } = getSocialMediaConnectionErrorDisplay(err)
      if (NO_PAGES_CODES.includes(code)) {
        this.platformError[platform] = code
      } else {
        this.showConnectionErrorSnackbar({ severity, messageKey, platform })
      }
      this.$tracking.event('Content Creator', 'Connected', platform, 'Failed')
    },
    connectedSuccessfully(ok) {
      if (!ok || !this.connectingPlatform) return
      if (this.connectingPlatform === this.googleBusinessPlatformLabel) return
      this.$tracking.event('Content Creator', 'Connected', this.connectingPlatform)
      showSnackbarMessage(
        'success',
        this.$t('alerts.social-media.connect-to-platform.success', {
          platform: this.connectingPlatform,
        })
      )
    },
  },
  methods: {
    showConnectionErrorSnackbar({ severity, messageKey, platform }) {
      showSnackbarMessage(
        severity === 'WARNING' ? 'warning' : 'error',
        messageKey
          ? this.$t(`alerts.social-media.connection-error.${messageKey}`, { platform })
          : this.$t('alerts.social-media.connect-to-platform.error', { platform }),
        [this.helpCenterLinkText]
      )
    },
    close() {
      this.dialog = false
      this.platformError = {}
      this.$emit('close')
    },
    open() {
      this.dialog = true
    },
    async connectGoogleBusiness() {
      const platform = this.googleBusinessPlatformKey
      const platformLabel = this.googleBusinessPlatformLabel
      this.connectingPlatform = platformLabel
      this.platformError[platform] = null
      try {
        await this.connectToGoogleBusiness()
        if (this.composableError) throw this.composableError
        await this.refreshGoogleBusiness()
        if (!this.isGoogleBusinessConnected) throw new Error('Google Business is not connected')
        this.$tracking.event('Content Creator', 'Connected', platform)
        showSnackbarMessage(
          'success',
          this.$t('alerts.social-media.connect-to-platform.success', { platform: platformLabel })
        )
      } catch (err) {
        const { severity, messageKey } = getSocialMediaConnectionErrorDisplay(err)
        if (messageKey)
          this.showConnectionErrorSnackbar({ severity, messageKey, platform: platformLabel })
        else this.platformError[platform] = true
        this.$tracking.event('Content Creator', 'Connected', platform, 'Failed')
      }
    },
    async selectGoogleBusinessLocation(channelId) {
      await this.selectOption(SocialMediaType.GoogleBusiness, channelId)
      await this.refreshGoogleBusiness()
    },
    async refreshGoogleBusiness() {
      await this.loadGoogleBusinessStatus()
      if (!this.isGoogleBusinessConnected) return
      await this.loadGoogleBusinessLocations()
    },
    async connectLinkedin() {
      this.connectingPlatform = 'LinkedIn'
      this.platformError.LinkedIn = null
      return this.connectToLinkedin()
    },
    async connectFacebook() {
      this.connectingPlatform = 'Facebook'
      this.platformError.Facebook = null
      return this.connectToFacebook()
    },
    async connectInstagram() {
      this.connectingPlatform = 'Instagram'
      this.platformError.Instagram = null
      return this.connectToInstagram()
    },
    // selectOption() (composable) has no error handling of its own — the old
    // SocialMediaPageSelection.vue caught rejections and surfaced the generic snackbar,
    // so these wrappers reproduce that instead of letting a failed mutation become an
    // unhandled rejection, and track a per-platform loading flag for the picker's spinner.
    async selectFacebookPage(optionId) {
      this.selectingOption.Facebook = true
      try {
        await this.selectOption(SocialMediaType.Facebook, optionId)
      } catch {
        this.showConnectionErrorSnackbar({ messageKey: null, platform: 'Facebook' })
      } finally {
        this.selectingOption.Facebook = false
      }
    },
    async selectInstagramPage(optionId) {
      this.selectingOption.Instagram = true
      try {
        await this.selectOption(SocialMediaType.Instagram, optionId)
      } catch {
        this.showConnectionErrorSnackbar({ messageKey: null, platform: 'Instagram' })
      } finally {
        this.selectingOption.Instagram = false
      }
    },
    async selectLinkedinPage(optionId) {
      this.selectingOption.LinkedIn = true
      try {
        await this.selectOption(SocialMediaType.Linkedin, optionId)
      } catch {
        this.showConnectionErrorSnackbar({ messageKey: null, platform: 'LinkedIn' })
      } finally {
        this.selectingOption.LinkedIn = false
      }
    },
  },
  emits: ['close'],
}
</script>

<style scoped>
.link-accounts-title {
  font-size: 22px;
  font-style: normal;
  font-weight: 500;
  line-height: 32px;
}
.add-account {
  font-size: 12px;
  font-style: normal;
  line-height: 16px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
.connected-pages {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
}

.proceed-btn,
.close-btn {
  font-size: 14px !important;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
</style>
